import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseconfig';
import { FaBuilding, FaMapMarkerAlt, FaPhone, FaClock, FaStar, FaTools, FaArrowLeft } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const RepairCenter = () => {
  const { id } = useParams();
  const [serviceCenter, setServiceCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);

  useEffect(() => {
    const fetchServiceCenter = async () => {
      try {
        if (!id) {
          setError('Please select a service center from the list');
          setLoading(false);
          return;
        }

        const serviceCenterDoc = await getDoc(doc(db, 'users', id));
        
        if (serviceCenterDoc.exists()) {
          const data = serviceCenterDoc.data();
          setServiceCenter({
            displayName: data.displayName || '',
            businessName: data.businessName || '',
            businessAddress: data.businessAddress || '',
            phone: data.phone || '',
            services: data.services || [],
            openingHours: data.openingHours || 'Not specified',
            rating: data.rating || 0,
            reviewCount: data.reviewCount || 0,
            description: data.description || 'No description provided',
            specialties: data.specialties || []
          });
          
          if (data.location) {
            setPosition({ lat: data.location.lat, lng: data.location.lng });
          }
        } else {
          throw new Error('Service center not found');
        }
      } catch (err) {
        console.error('Error fetching service center:', err);
        setError(`Failed to load service center data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceCenter();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent align-[-0.125em]"></div>
          <p className="mt-2 text-gray-700">Loading service center details...</p>
        </div>
      </div>
    );
  }

  if (error || !serviceCenter) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md">
          <div className="text-red-600 text-center mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold mt-2">Error</h2>
          </div>
          <p className="text-center">{error || 'Service center information could not be loaded'}</p>
          <div className="mt-6 flex justify-center">
            <Link 
              to="/repair-centers"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <FaArrowLeft className="mr-2" /> Back to Repair Centers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const defaultCenter = { lat: 7.8731, lng: 80.7718 }; // Default center (Sri Lanka)
  
  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link 
            to="/repair-centers"
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            <FaArrowLeft className="mr-2" /> Back to Repair Centers
          </Link>
        </div>
        
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="bg-blue-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">{serviceCenter.businessName}</h1>
            <p className="text-blue-100">{serviceCenter.displayName}</p>
          </div>

          <div className="p-6">
            <div className="flex items-center mb-6">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar 
                    key={star}
                    className={`${
                      star <= Math.round(serviceCenter.rating) 
                        ? 'text-yellow-500' 
                        : 'text-gray-300'
                    } h-5 w-5`} 
                  />
                ))}
              </div>
              <span className="ml-2 text-gray-600">
                {serviceCenter.rating.toFixed(1)} ({serviceCenter.reviewCount} reviews)
              </span>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">About This Service Center</h2>
              <p className="text-gray-600">{serviceCenter.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-3">Contact Information</h2>
                  
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <FaBuilding className="text-blue-500 mt-1 mr-3" />
                      <div>
                        <p className="font-medium">Business Address</p>
                        <p className="text-gray-600">{serviceCenter.businessAddress}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <FaPhone className="text-blue-500 mr-3" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <p className="text-gray-600">{serviceCenter.phone || 'Not provided'}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <FaClock className="text-blue-500 mr-3" />
                      <div>
                        <p className="font-medium">Business Hours</p>
                        <p className="text-gray-600">{serviceCenter.openingHours}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-3">Services Offered</h2>
                  {serviceCenter.services && serviceCenter.services.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {serviceCenter.services.map((service, index) => (
                        <div key={index} className="flex items-center bg-blue-50 p-3 rounded-md">
                          <FaTools className="text-blue-500 mr-2" />
                          <span className="text-gray-700">{service}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No services listed</p>
                  )}
                </div>
                
                {serviceCenter.specialties && serviceCenter.specialties.length > 0 && (
                  <div className="mt-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-3">Specialties</h2>
                    <div className="flex flex-wrap gap-2">
                      {serviceCenter.specialties.map((specialty, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Location</h2>
                <div className="h-64 md:h-80 w-full rounded-lg overflow-hidden border border-gray-300">
                  <MapContainer
                    center={position || defaultCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {position && <Marker position={position} />}
                  </MapContainer>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Book a Repair Service</h2>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-gray-700 mb-4">
                  Ready to get your device repaired? Schedule an appointment with {serviceCenter.businessName} to get started.
                </p>
                <button 
                  className="w-full sm:w-auto bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                  onClick={() => window.location.href = `/book-service/${id}`}
                >
                  <span>Schedule Appointment</span>
                  <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairCenter;